// Calculator pentru programe de antrenament pe bandă bazat pe intensitate
export const generateWorkoutProgram = (userProfile, intensity) => {
  if (!userProfile) return null;

  // Calculează lungimea pasului
  const stepLength = calculateStepLength(userProfile.sex, userProfile.height_cm);
  
  // Determină parametrii în funcție de profil și intensitate
  const workoutParams = getWorkoutParameters(userProfile, intensity);
  
  // Calculează vitezele în funcție de profilul utilizatorului
  const speeds = calculateSpeeds(userProfile, intensity);
  
  // Calculează durata totală (furnizată direct din tabel)
  const totalTimeMin = workoutParams.duration;
  
  // --- CONFIGURARE FAZE ---
  let warmupTime, cooldownTime, intenseTime;

  if (intensity === 'start') {
    // Pentru "Start", avem o structură specială fixă de 10 minute total
    warmupTime = 2; // 2 min încălzire
    cooldownTime = 2; // 2 min răcire
    intenseTime = 6; // 6 min mers
    // Total = 10 min
  } else {
    // Pentru restul, păstrăm regula de "Încălzire 10 minute" cerută anterior
    warmupTime = 10;
    
    // Calculăm restul
    let remainingTime = totalTimeMin - warmupTime;
    
    // Safety check
    if (remainingTime < 2) remainingTime = 5;

    // Împărțim timpul rămas
    cooldownTime = Math.max(2, Math.round(remainingTime * 0.2)); 
    intenseTime = Math.max(1, remainingTime - cooldownTime);
  }
  
  // Recalculăm timpul total real
  const realTotalTime = warmupTime + intenseTime + cooldownTime;

  // Calculează pașii pentru fiecare fază
  const warmupSteps = timeSpeedToSteps(warmupTime, speeds.warmup, stepLength);
  const intenseSteps = timeSpeedToSteps(intenseTime, speeds.intense, stepLength);
  const cooldownSteps = timeSpeedToSteps(cooldownTime, speeds.cooldown, stepLength);

  // Total pași estimati și distanță
  const totalSteps = warmupSteps + intenseSteps + cooldownSteps;
  const distance_m = totalSteps * stepLength;
  const distance_km = distance_m / 1000;
  
  // Ajustare viteze endSpeed pentru Start (să fie constant)
  const intenseEndSpeed = intensity === 'start' ? speeds.intense : speeds.intense;
  const warmupEndSpeed = intensity === 'start' ? speeds.intense : parseFloat((speeds.intense * 0.8).toFixed(1));

  return {
    intensity,
    stepsTarget: totalSteps,
    distance_km: parseFloat(distance_km.toFixed(2)),
    total_time_min: realTotalTime,
    phases: [
      {
        name: 'warmup',
        duration_min: warmupTime,
        startSpeed_kmh: speeds.warmup,
        endSpeed_kmh: warmupEndSpeed,
        estimatedSteps: warmupSteps
      },
      {
        name: 'intense',
        duration_min: intenseTime,
        startSpeed_kmh: speeds.intense,
        endSpeed_kmh: intenseEndSpeed,
        estimatedSteps: intenseSteps
      },
      {
        name: 'cooldown',
        duration_min: cooldownTime,
        startSpeed_kmh: intensity === 'start' ? speeds.intense : parseFloat((speeds.intense * 0.8).toFixed(1)),
        endSpeed_kmh: speeds.cooldown,
        estimatedSteps: cooldownSteps
      }
    ]
  };
};

// Tabelul de intensități conform specificațiilor
const getWorkoutParameters = (userProfile, intensity) => {
  const { fitnessLevel } = userProfile;
  
  // Configurație specială pentru START (10 minute fix)
  const startConfig = { duration: 10, steps: 1000 };

  const intensityTable = {
    sedentar: {
      start: startConfig,
      usor: { duration: 15, steps: 1500 },
      mediu: { duration: 20, steps: 2500 },
      intens: { duration: 25, steps: 3250 }
    },
    incepator: {
      start: startConfig,
      usor: { duration: 25, steps: 2500 },
      mediu: { duration: 30, steps: 3500 },
      intens: { duration: 35, steps: 4500 }
    },
    intermediar: {
      start: startConfig,
      usor: { duration: 30, steps: 3250 },
      mediu: { duration: 40, steps: 4500 },
      intens: { duration: 45, steps: 5500 }
    },
    avansat: {
      start: startConfig,
      usor: { duration: 40, steps: 3750 },
      mediu: { duration: 50, steps: 5750 },
      intens: { duration: 60, steps: 8000 }
    }
  };
  
  const levelData = intensityTable[fitnessLevel] || intensityTable.incepator;
  return levelData[intensity] || levelData.mediu;
};

const calculateStepLength = (sex, heightCm) => {
  const multiplier = sex === 'femeie' ? 0.41 : 0.45;
  return (heightCm * multiplier) / 100;
};

const calculateIMC = (weightKg, heightCm) => {
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
};

const calculateSpeeds = (userProfile, intensity) => {
  const { age, weight_kg, height_cm, fitnessLevel } = userProfile;
  const imc = calculateIMC(weight_kg, height_cm);
  
  const baseSpeeds = {
    start: { warmup: 2.5, intense: 3.5, cooldown: 2.5 }, // Foarte ușor pentru start
    usor: { warmup: 3.0, intense: 4.0, cooldown: 3.0 },
    mediu: { warmup: 3.5, intense: 5.0, cooldown: 3.5 },
    intens: { warmup: 4.0, intense: 6.0, cooldown: 4.0 }
  };
  
  const fitnessMultipliers = {
    sedentar: 0.8,
    incepator: 0.9,
    intermediar: 1.0,
    avansat: 1.1
  };
  
  const agePenalty = age >= 60 ? 0.9 : age >= 40 ? 0.95 : 1.0;
  const imcPenalty = imc >= 30 ? 0.8 : imc >= 25 ? 0.9 : 1.0;
  
  const multiplier = (fitnessMultipliers[fitnessLevel] || 1) * agePenalty * imcPenalty;
  const base = baseSpeeds[intensity] || baseSpeeds.mediu;
  
  return {
    warmup: parseFloat((base.warmup * multiplier).toFixed(1)),
    intense: parseFloat((base.intense * multiplier).toFixed(1)),
    cooldown: parseFloat((base.cooldown * multiplier).toFixed(1))
  };
};

const timeSpeedToSteps = (timeMin, speedKmh, stepLengthM) => {
  const distanceKm = (speedKmh * timeMin) / 60;
  const distanceM = distanceKm * 1000;
  return Math.round(distanceM / stepLengthM);
};

export { calculateStepLength, calculateIMC };